import { isIOS, isAndroid } from './pwaUtils';

// Определяем операционную систему
export const detectOS = () => {
    if (isIOS()) {
        return 'iOS';
    } else if (isAndroid()) {
        return 'Android';
    } else if (navigator.userAgent.toLowerCase().includes('windows')) {
        return 'Windows';
    } else if (navigator.userAgent.toLowerCase().includes('mac')) {
        return 'macOS';
    } else if (navigator.userAgent.toLowerCase().includes('linux')) {
        return 'Linux';
    } else {
        return 'Неизвестная ОС';
    }
};

// Расширенная проверка Telegram WebView API
// ВАЖНО: Telegram WebView маскируется под Safari, особенно на iOS
export const checkTelegramAPI = () => {
    if (typeof window === 'undefined') return false;

    // Упрощенная проверка: если есть TelegramWebviewProxy или TelegramWebview - это точно Telegram
    if (window.TelegramWebviewProxy || window.TelegramWebview) {
        return true;
    }

    // ВТОРОЙ ПРИОРИТЕТ: Проверка window.Telegram.WebApp
    if (!window.Telegram ||
        !window.Telegram.WebApp ||
        typeof window.Telegram.WebApp !== 'object') {
        return false;
    }

    const webApp = window.Telegram.WebApp;

    // Проверяем специфичные свойства/методы Telegram WebApp API:

    // 1. Метод ready() - обязательный для Telegram WebApp API
    const hasReadyMethod = typeof webApp.ready === 'function';

    // 2. Метод expand() - специфично для Telegram
    const hasExpandMethod = typeof webApp.expand === 'function';

    // 3. Свойство isExpanded - специфично для Telegram
    const hasIsExpanded = typeof webApp.isExpanded === 'boolean';

    // 4. Метод close() или closeWebApp() - специфично для Telegram
    const hasCloseMethod = typeof webApp.close === 'function' ||
        typeof webApp.closeWebApp === 'function';

    // 5. Свойство version - обычно есть в Telegram WebApp
    const hasVersion = typeof webApp.version === 'string' && webApp.version.length > 0;

    // 6. Свойство platform - обычно есть в Telegram WebApp
    const hasPlatform = typeof webApp.platform === 'string' && webApp.platform.length > 0;

    // 7. Свойство initData или initDataUnsafe - специфично для Telegram WebApp
    const hasInitData = typeof webApp.initData === 'string' ||
        (typeof webApp.initDataUnsafe === 'object' && webApp.initDataUnsafe !== null);

    // 8. Свойство colorScheme - специфично для Telegram WebApp
    const hasColorScheme = typeof webApp.colorScheme === 'string';

    // 9. Свойство themeParams - специфично для Telegram WebApp
    const hasThemeParams = typeof webApp.themeParams === 'object' && webApp.themeParams !== null;

    // Подсчитываем признаки
    const signs = [
        hasReadyMethod,
        hasExpandMethod,
        hasIsExpanded,
        hasCloseMethod,
        hasVersion,
        hasPlatform,
        hasInitData,
        hasColorScheme,
        hasThemeParams
    ].filter(Boolean).length;

    // Если есть хотя бы 2 признака - это скорее всего Telegram WebApp API
    // (на iOS может быть меньше признаков доступно сразу)
    if (signs >= 2) {
        return true;
    }

    // Если есть хотя бы метод ready() и ещё один специфичный признак
    if (hasReadyMethod && (hasExpandMethod || hasCloseMethod || hasVersion || hasPlatform || hasInitData)) {
        return true;
    }

    return false;
};

// Дополнительные проверки для Telegram WebView (особенно на iOS)
export const checkTelegramAdditional = () => {
    if (typeof window === 'undefined') return false;

    // 1. ПРИОРИТЕТ: Проверяем TelegramWebviewProxy или TelegramWebview
    if (window.TelegramWebviewProxy || window.TelegramWebview) {
        return true;
    }

    // 2. Проверяем наличие специфичных объектов Telegram (даже если основной API ещё не загружен)
    if (window.Telegram && typeof window.Telegram === 'object') {
        // Если есть объект Telegram, даже без WebApp - это может быть Telegram
        // Но проверяем только если это точно не Safari
        const isLikelySafari = navigator.userAgent.includes('Safari') &&
            !navigator.userAgent.includes('Chrome') &&
            !navigator.userAgent.includes('Instagram');

        // Если это похоже на Safari и нет WebApp - пропускаем
        if (isLikelySafari && !window.Telegram.WebApp) {
            return false;
        }

        // Если есть объект Telegram - это может быть Telegram WebView
        if (window.Telegram.WebApp || window.Telegram.WebView) {
            return true;
        }
    }

    // 3. Проверяем специфичные методы через try-catch
    try {
        // Пытаемся вызвать методы Telegram API (если они есть)
        if (window.Telegram?.WebApp?.ready && typeof window.Telegram.WebApp.ready === 'function') {
            // Просто проверяем наличие, не вызываем
            return true;
        }
    } catch (e) {
        // Игнорируем ошибки
    }

    return false;
};

// Определяем браузер
export const detectBrowser = () => {
    const userAgent = navigator.userAgent;
    const userAgentLower = userAgent.toLowerCase();

    // 0. ПРОВЕРЯЕМ TELEGRAM WEBAPP API ПЕРВЫМ - это самый надежный способ!
    // Telegram WebView маскируется под Safari, но имеет уникальный API
    if (checkTelegramAPI() || checkTelegramAdditional()) {
        return 'Telegram WebView';
    }

    // 1. Проверяем Instagram WebView - по специфичным признакам
    // Instagram WebView имеет в User Agent строку вида "Instagram 407.0.0.31.80" или "IABMV/1"
    if (userAgentLower.includes('instagram') ||
        userAgentLower.includes('iabmv') ||
        userAgentLower.includes('fbav') ||
        (userAgentLower.includes('fban') && !userAgentLower.includes('telegram'))) {
        return 'Instagram WebView';
    }

    // 2. Проверяем Edge - по специфичному признаку "Edg/"
    if (userAgentLower.includes('edg/') ||
        (userAgentLower.includes('edg') && userAgentLower.includes('chrome'))) {
        return 'Edge';
    }

    // 3. Проверяем Chrome (включая Chrome на iOS - crios)
    // Chrome имеет "Chrome/" в User Agent (даже если там есть "Safari/")
    // Chrome на iOS имеет "CriOS/" в User Agent
    if ((userAgentLower.includes('chrome/') && !userAgentLower.includes('edg/')) ||
        userAgentLower.includes('crios/')) {
        return 'Chrome';
    }

    // 4. Проверяем Safari (после всех встроенных браузеров и Telegram)
    // Safari имеет "Safari/" и "Version/" в User Agent, но не имеет "Chrome/", "CriOS/", "Edg/"
    // Также может содержать "Mobile/" на мобильных устройствах
    // ВАЖНО: Telegram WebView тоже выглядит как Safari, но мы уже проверили Telegram выше
    // Повторно проверяем Telegram перед определением Safari (на случай, если API еще не загрузился)
    if ((userAgentLower.includes('safari/') || userAgentLower.includes('version/')) &&
        !userAgentLower.includes('chrome/') &&
        !userAgentLower.includes('crios/') &&
        !userAgentLower.includes('edg/') &&
        !userAgentLower.includes('opr/') &&
        !userAgentLower.includes('firefox/') &&
        !userAgentLower.includes('instagram') &&
        !userAgentLower.includes('iabmv')) {
        // Перед определением Safari еще раз проверяем Telegram (включая дополнительные проверки)
        if (checkTelegramAPI() || checkTelegramAdditional()) {
            return 'Telegram WebView';
        }
        return 'Safari';
    }

    // 5. Проверяем Firefox
    if (userAgentLower.includes('firefox/')) {
        return 'Firefox';
    }

    // 6. Проверяем Opera
    if (userAgentLower.includes('opr/') ||
        (userAgentLower.includes('opera/') && !userAgentLower.includes('opr/'))) {
        return 'Opera';
    }

    // 7. Повторная проверка Telegram - если явно упомянут в User Agent
    // Проверяем явное упоминание Telegram в User Agent (не просто "tg")
    const hasTelegramInUA = userAgentLower.includes('telegram') ||
        userAgentLower.includes('telegrambot') ||
        userAgentLower.includes('telegramwebapp');

    if (hasTelegramInUA &&
        !userAgentLower.includes('instagram') &&
        !userAgentLower.includes('iabmv')) {
        return 'Telegram WebView';
    }

    return 'Неизвестный браузер';
};

// Функция для определения браузера с повторными проверками
export const detectBrowserWithRetry = () => {
    const detected = detectBrowser();

    // Если определили как Safari, делаем повторную проверку Telegram API
    // (API может загружаться асинхронно, особенно на iOS)
    if (detected === 'Safari' && typeof window !== 'undefined') {
        // Проверяем Telegram API и дополнительные признаки
        if (checkTelegramAPI() || checkTelegramAdditional()) {
            return 'Telegram WebView';
        }
    }

    return detected;
};
